import {
  DESPATCH_FLOW_LABELS,
  type DespatchSummary,
  INVOICE_STATUS_LABELS,
  type InvoiceFlow,
  invoiceFlowSchema,
  invoiceStatusSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  type DataTableColumn,
  DateInput,
  EmptyState,
  FormField,
  Input,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { FilePlus2, Truck } from "lucide-react";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../../features/accounting/company.js";
import { CounterpartyIdCombobox } from "../../../../features/counterparties/counterparty-combobox.js";
import {
  EdocumentStatusBadge,
  InvoiceStatusBadge,
  InvoicingPage,
  ReceiptAdviceBadge,
  useInvoicingCrumb,
  useInvoicingPermissions,
} from "../../../../features/invoicing/shared.js";
import { api } from "../../../../lib/api.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDate } from "../../../../lib/format.js";
import { queryKeys } from "../../../../lib/query.js";

const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);
const searchSchema = z.object({
  ...companySearchShape,
  tab: invoiceFlowSchema.optional().catch(undefined),
  status: invoiceStatusSchema.optional().catch(undefined),
  counterpartyId: z.string().uuid().optional().catch(undefined),
  from: date,
  to: date,
  q: z.string().max(100).optional().catch(undefined),
});
type DespatchSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/faturalar/irsaliyeler/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: DespatchListPage,
});

const FLOWS: InvoiceFlow[] = ["OUTGOING", "INCOMING"];

function DespatchListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const crumb = useInvoicingCrumb("/faturalar");
  const permissions = useInvoicingPermissions();
  const tab = search.tab ?? "OUTGOING";

  return (
    <InvoicingPage
      title={t("menu.despatches")}
      description={t("invoicing.despatch.listDescription")}
      breadcrumb={[crumb, { label: t("menu.despatches") }]}
      actions={
        permissions.canManageDespatch ? (
          <Button asChild>
            <Link to="/faturalar/irsaliyeler/yeni" search={{ flow: tab }}>
              <FilePlus2 aria-hidden="true" />
              {t("invoicing.despatch.new")}
            </Link>
          </Button>
        ) : null
      }
    >
      {({ companyId }) => (
        <Tabs
          value={tab}
          onValueChange={(next) =>
            void navigate({
              search: (previous) => ({ ...previous, tab: next as InvoiceFlow }),
              replace: true,
            })
          }
        >
          <TabsList aria-label={t("invoicing.despatch.tabs")}>
            {FLOWS.map((flow) => (
              <TabsTrigger key={flow} value={flow}>
                {DESPATCH_FLOW_LABELS[flow]}
              </TabsTrigger>
            ))}
          </TabsList>
          {FLOWS.map((flow) => (
            <TabsContent key={flow} value={flow}>
              {tab === flow ? (
                <DespatchList key={flow} companyId={companyId} flow={flow} search={search} />
              ) : null}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </InvoicingPage>
  );
}

function DespatchList({
  companyId,
  flow,
  search,
}: {
  companyId: string;
  flow: InvoiceFlow;
  search: DespatchSearch;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const [searchText, setSearchText] = useState(search.q ?? "");
  const setFilters = (patch: Partial<DespatchSearch>) =>
    void navigate({
      search: (previous) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value)),
      replace: true,
    });

  useEffect(() => {
    const handle = setTimeout(() => {
      if ((search.q ?? "") !== searchText.trim()) setFilters({ q: searchText.trim() || undefined });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca yazılan metin değişince çalışmalı
  }, [searchText]);

  const query = {
    companyId,
    limit: 25,
    flow,
    ...(search.status ? { status: search.status } : {}),
    ...(search.counterpartyId ? { counterpartyId: search.counterpartyId } : {}),
    ...(search.from ? { from: search.from } : {}),
    ...(search.to ? { to: search.to } : {}),
    ...(search.q ? { search: search.q } : {}),
  };
  const pages = useCursorPages(JSON.stringify(query));
  const list = useQuery({
    queryKey: queryKeys.invoicing(companyId, "despatches", { ...query, cursor: pages.cursor ?? null }),
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (
        await api.GET("/despatch-advices", {
          params: { query: { ...query, ...(pages.cursor ? { cursor: pages.cursor } : {}) } },
        })
      ).data!,
  });

  const columns: DataTableColumn<DespatchSummary>[] = [
    {
      id: "despatchNo",
      header: t("invoicing.despatch.despatchNo"),
      hideable: false,
      cell: (row) => (
        <Link
          to="/faturalar/irsaliyeler/$id"
          params={{ id: row.id }}
          className="font-mono text-primary underline-offset-4 hover:underline"
        >
          {row.despatchNo ?? t("invoicing.list.draftNo")}
        </Link>
      ),
      exportValue: (row) => row.despatchNo ?? "",
    },
    {
      id: "issueDate",
      header: t("invoicing.fields.issueDate"),
      cell: (row) => <span className="tabular">{formatDate(row.issueDate)}</span>,
      exportValue: (row) => row.issueDate,
    },
    {
      id: "counterparty",
      header: t("invoicing.fields.counterparty"),
      cell: (row) => row.counterpartyName,
      exportValue: (row) => row.counterpartyName,
    },
    {
      id: "vehiclePlate",
      header: t("invoicing.despatch.vehiclePlate"),
      cell: (row) => row.vehiclePlate ?? "—",
      exportValue: (row) => row.vehiclePlate ?? "",
    },
    {
      id: "lineCount",
      header: t("invoicing.despatch.lineCount"),
      align: "right",
      cell: (row) => row.lineCount,
      exportValue: (row) => row.lineCount,
    },
    {
      id: "status",
      header: t("common.status"),
      cell: (row) => <InvoiceStatusBadge status={row.status} />,
      exportValue: (row) => row.status,
    },
    {
      id: "edocumentStatus",
      header: t("invoicing.fields.edocumentStatus"),
      cell: (row) => <EdocumentStatusBadge status={row.edocumentStatus} error={row.edocumentError} />,
      exportValue: (row) => row.edocumentStatus,
    },
    {
      id: "receiptAdvice",
      header: t("invoicing.despatch.receiptAdvice"),
      cell: (row) => <ReceiptAdviceBadge status={row.receiptAdviceStatus} />,
      exportValue: (row) => row.receiptAdviceStatus,
    },
    {
      id: "invoices",
      header: t("invoicing.despatch.linkedInvoices"),
      cell: (row) =>
        row.invoiceIds.length === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <span className="flex flex-wrap gap-1">
            {row.invoiceIds.map((invoiceId) => (
              <Link
                key={invoiceId}
                to="/faturalar/$id"
                params={{ id: invoiceId }}
                className="text-primary underline-offset-4 hover:underline"
              >
                {t("invoicing.despatch.invoiceLink")}
              </Link>
            ))}
          </span>
        ),
      exportValue: (row) => row.invoiceIds.length,
    },
  ];

  return (
    <DataTable
      caption={t("invoicing.despatch.caption", { flow: DESPATCH_FLOW_LABELS[flow] })}
      columns={columns}
      rows={list.data?.items ?? []}
      rowKey={(row) => row.id}
      loading={list.isPending}
      error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
      emptyState={
        <EmptyState
          headingLevel={2}
          icon={<Truck />}
          title={t("invoicing.despatch.emptyTitle")}
          description={t("invoicing.despatch.emptyDescription")}
        />
      }
      filters={
        <>
          <FormField label={t("common.status")} className="w-40">
            {(control) => (
              <Select
                {...control}
                value={search.status ?? ""}
                onChange={(event) =>
                  setFilters({
                    status:
                      event.target.value === "" ? undefined : invoiceStatusSchema.parse(event.target.value),
                  })
                }
              >
                <option value="">{t("common.all")}</option>
                {invoiceStatusSchema.options.map((status) => (
                  <option key={status} value={status}>
                    {INVOICE_STATUS_LABELS[status]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("invoicing.fields.counterparty")} className="w-64">
            {(control) => (
              <CounterpartyIdCombobox
                {...control}
                companyId={companyId}
                counterpartyId={search.counterpartyId ?? null}
                onChange={(id) => setFilters({ counterpartyId: id ?? undefined })}
              />
            )}
          </FormField>
          <FormField label={t("invoicing.list.from")} className="w-40">
            {(control) => (
              <DateInput
                {...control}
                value={search.from ?? null}
                onValueChange={(value) => setFilters({ from: value ?? undefined })}
              />
            )}
          </FormField>
          <FormField label={t("invoicing.list.to")} className="w-40">
            {(control) => (
              <DateInput
                {...control}
                value={search.to ?? null}
                onValueChange={(value) => setFilters({ to: value ?? undefined })}
              />
            )}
          </FormField>
          <FormField label={t("common.search")} className="w-56">
            {(control) => (
              <Input
                {...control}
                type="search"
                value={searchText}
                placeholder={t("invoicing.despatch.searchPlaceholder")}
                onChange={(event) => setSearchText(event.target.value)}
              />
            )}
          </FormField>
        </>
      }
      pagination={{
        hasPrevious: pages.hasPrevious,
        hasNext: list.data?.page.hasMore ?? false,
        onPrevious: pages.previous,
        onNext: () => pages.next(list.data?.page.nextCursor),
        pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
      }}
    />
  );
}
